/**
 * Super Admin Router
 *
 * Routes for the super admin portal with nested layout structure.
 * All authenticated routes are nested under SuperAdminLayout for consistent
 * sidebar and navigation.
 */
import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Login } from '../pages/auth';
import { Dashboard } from '../pages';
import { SuperAdminLayout } from '../layouts/SuperAdminLayout';
import { AccountsList, AccountDetail, CreateAccount } from '../pages/accounts';
import { SupportInbox } from '../pages/support';
import { BillingDashboard } from '../pages/billing';
import { PlanSetupPage } from '../pages/plan-setup';
import {
  TemplatesList,
  TemplateEditor,
  FormTemplatesList,
  FormTemplateEditor,
  DocumentTemplatesList,
  DocumentTemplateEditor,
  CategoriesManager,
  DocAndFormTemplates,
} from '../pages/templates';
import { CampaignsList, CampaignBuilder, CampaignDetail } from '../pages/campaigns';
import { OperationsDashboard } from '../pages/operations';
import { OpsAgentConsole } from '../pages/ops-agents';
import { AiOperations } from '../pages/ai-operations';
import { ChatWidgets } from '../pages/chat-widgets';
import { LiveChats } from '../pages/live-chats';
import { ChatLeads } from '../pages/chat-leads';
import { NumberInventory } from '../pages/inventory';
import { AuditLogs } from '../pages/audit';
import { UsageDashboard } from '../pages/usage/UsageDashboard';
import {
  SettingsPage,
  AdminUsers,
  FeatureFlags,
  ComplianceSettings,
  PolicyEngine,
  PolicyDocs,
  PolicyJurisdictions,
  PolicyConsentCapture,
  PolicyCheckLibrary,
  PolicyCallingHours,
  PolicyDNC,
  PolicyAIClassification,
  PolicyDecisionLog,
  PreBundleManager,
  InternalDnc,
  SmsTemplates,
  SmsSenders,
  EmailTemplates,
  PromptTemplates,
} from '../pages/settings';

// Root route - just renders the outlet
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Login route - public, no layout
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

// Layout route - wraps all authenticated routes with SuperAdminLayout
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: SuperAdminLayout,
});

// Redirect root to dashboard
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

// Dashboard route - main landing page
const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: Dashboard,
});

// Accounts & Users routes - Phase 2
const accountsIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts',
  component: AccountsList,
});

const accountsNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts/new',
  component: CreateAccount,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts/$id',
  component: AccountDetail,
});

const accountDetailTabRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/accounts/$id/$tab',
  component: AccountDetail,
});

// Support Inbox route - Phase 3
const supportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support',
  component: SupportInbox,
});

// Templates routes - Phase 5
const templatesIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates',
  component: TemplatesList,
});

const templatesNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/new',
  component: TemplateEditor,
});

const templatesEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/$id/edit',
  component: TemplateEditor,
});

// Form Templates routes - Phase 5
const formTemplatesIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/forms',
  component: FormTemplatesList,
});

const formTemplatesNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/forms/new',
  component: FormTemplateEditor,
});

const formTemplatesEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/forms/$id/edit',
  component: FormTemplateEditor,
});

// Document Templates routes - Phase 5
const documentTemplatesIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/documents',
  component: DocumentTemplatesList,
});

const documentTemplatesNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/documents/new',
  component: DocumentTemplateEditor,
});

const documentTemplatesEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/documents/$id/edit',
  component: DocumentTemplateEditor,
});

// Combined Document & Form Templates route
const docAndFormTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/doc-and-form',
  component: DocAndFormTemplates,
});

// Template Categories route - Dynamic category management
const templateCategoriesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/templates/categories',
  component: CategoriesManager,
});

// Campaigns routes - Phase 5
const campaignsIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns',
  component: CampaignsList,
});

const campaignsNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/new',
  component: CampaignBuilder,
});

const campaignsEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/$id/edit',
  component: CampaignBuilder,
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/$id',
  component: CampaignDetail,
});

const campaignDetailTabRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns/$id/$tab',
  component: CampaignDetail,
});

// Plans & Billing routes
const billingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/billing',
  component: BillingDashboard,
});

// Unified Plan Setup page
const planSetupRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plan-setup',
  component: PlanSetupPage,
});

// Redirect old plan routes → /plan-setup
const plansIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plans',
  beforeLoad: () => { throw redirect({ to: '/plan-setup' }); },
});

const plansNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plans/new',
  beforeLoad: () => { throw redirect({ to: '/plan-setup' }); },
});

const plansEditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plans/$id/edit',
  beforeLoad: () => { throw redirect({ to: '/plan-setup' }); },
});

// Audit Logs route - Phase 6
const auditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/audit',
  component: AuditLogs,
});

// Platform Operations route - Phase 6
const operationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/operations',
  component: OperationsDashboard,
});

// AI Console — chat with the internal ops agents (tools execute live)
const opsAgentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/ai-console',
  component: OpsAgentConsole,
});

// AI Operations — model config + usage/cost analytics
const aiOperationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/ai-operations',
  component: AiOperations,
});

// Chat Widgets — per-widget knowledge, sample prompts and presentation for the
// assistant embedded on public sites. `?widget=` holds the open widget, so a
// reload comes back to the one being edited rather than to the first in the list
// — which read as "my uploaded documents disappeared".
const chatWidgetsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/chat-widgets',
  component: ChatWidgets,
  validateSearch: (search: Record<string, unknown>): { widget?: string } => ({
    widget: typeof search.widget === 'string' ? search.widget : undefined,
  }),
});

// Live Chats — the staff side of the website widget: read a conversation, take
// it over, reply, hand it back. `?session=` is the Teams card's deep link.
const liveChatsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/live-chats',
  component: LiveChats,
  validateSearch: (search: Record<string, unknown>): { session?: string } => ({
    session: typeof search.session === 'string' ? search.session : undefined,
  }),
});

// Chat Leads — everyone who left their details on a widget, from either the
// identity card or the offline contact form
const chatLeadsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/chat-leads',
  component: ChatLeads,
});

// Number Inventory — multi-tenant phone-number assignments
const numberInventoryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory',
  component: NumberInventory,
});

// Usage Analytics route
const usageRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/usage',
  component: UsageDashboard,
});

// Settings routes - Phase 7
const settingsIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsPage,
});

// Redirect /settings/add-ons → /plan-setup?tab=addons
const addOnsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/add-ons',
  beforeLoad: () => { throw redirect({ to: '/plan-setup', search: { tab: 'addons' } }); },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/users',
  component: AdminUsers,
});

const featureFlagsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/feature-flags',
  component: FeatureFlags,
});

const smsTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/sms-templates',
  component: SmsTemplates,
});

const smsSendersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/sms-senders',
  component: SmsSenders,
});

const emailTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/email-templates',
  component: EmailTemplates,
});

const promptTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/prompt-templates',
  component: PromptTemplates,
});

// Redirect /settings/helcim-plans → /plan-setup (page removed)
const helcimPlansRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/helcim-plans',
  beforeLoad: () => { throw redirect({ to: '/plan-setup' }); },
});

const complianceSettingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/compliance',
  component: ComplianceSettings,
});

// Policy Routing Engine routes - PRE Phase 2
const policyEngineRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy',
  component: PolicyEngine,
});

const policyDocsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/docs',
  component: PolicyDocs,
});

const policyJurisdictionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/jurisdictions',
  component: PolicyJurisdictions,
});

const policyConsentCaptureRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/consent-capture',
  component: PolicyConsentCapture,
});

const policyCheckLibraryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/checks',
  component: PolicyCheckLibrary,
});

const policyCallingHoursRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/hours',
  component: PolicyCallingHours,
});

const policyDNCRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/dnc',
  component: PolicyDNC,
});

const policyAIClassificationRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/ai',
  component: PolicyAIClassification,
});

const policyDecisionLogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/decisions',
  component: PolicyDecisionLog,
});

const preBundleRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/bundle',
  component: PreBundleManager,
});

// Internal DNC — platform-wide Do-Not-Contact list management
const internalDncRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/policy/dnc/internal',
  component: InternalDnc,
});

const aiCreditCostsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/ai-credit-costs',
  beforeLoad: () => { throw redirect({ to: '/plan-setup', search: { tab: 'ai-credits' } }); },
});

// Create route tree with nested structure
const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    accountsIndexRoute,
    accountsNewRoute,
    accountDetailRoute,
    accountDetailTabRoute,
    supportRoute,
    // Templates - Phase 5
    templatesIndexRoute,
    templatesNewRoute,
    templatesEditRoute,
    // Form Templates - Phase 5
    formTemplatesIndexRoute,
    formTemplatesNewRoute,
    formTemplatesEditRoute,
    // Document Templates - Phase 5
    documentTemplatesIndexRoute,
    documentTemplatesNewRoute,
    documentTemplatesEditRoute,
    // Combined Document & Form Templates
    docAndFormTemplatesRoute,
    // Template Categories - Dynamic category management
    templateCategoriesRoute,
    // Campaigns - Phase 5
    campaignsIndexRoute,
    campaignsNewRoute,
    campaignsEditRoute,
    campaignDetailRoute,
    campaignDetailTabRoute,
    // Billing & Plans
    billingRoute,
    planSetupRoute,
    plansIndexRoute,
    plansNewRoute,
    plansEditRoute,
    // Operations & Audit - Phase 6
    auditRoute,
    operationsRoute,
    opsAgentsRoute,
    aiOperationsRoute,
    chatWidgetsRoute,
    liveChatsRoute,
    chatLeadsRoute,
    numberInventoryRoute,
    usageRoute,
    // Settings - Phase 7
    settingsIndexRoute,
    addOnsRoute,
    adminUsersRoute,
    featureFlagsRoute,
    smsTemplatesRoute,
    smsSendersRoute,
    emailTemplatesRoute,
    promptTemplatesRoute,
    helcimPlansRoute,
    complianceSettingsRoute,
    // Policy Routing Engine - PRE Phase 2
    policyEngineRoute,
    policyDocsRoute,
    policyJurisdictionsRoute,
    policyConsentCaptureRoute,
    policyCheckLibraryRoute,
    policyCallingHoursRoute,
    policyDNCRoute,
    policyAIClassificationRoute,
    policyDecisionLogRoute,
    preBundleRoute,
    internalDncRoute,
    aiCreditCostsRoute,
  ]),
]);

// Create router instance
export const router = createRouter({ routeTree });

// Type registration for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
